/**
 * Centralized Client Personas
 *
 * Single source of truth for all testimonials, reviews, case studies, and avatars.
 * All 45 personas defined here - components import via helper functions.
 */

// ============================================================================
// TYPES
// ============================================================================

export type ClientNiche =
  | "fitness"
  | "fashion"
  | "beauty"
  | "travel"
  | "food"
  | "photography"
  | "business"
  | "art"
  | "tech"
  | "music"
  | "lifestyle"
  | "wellness"

export type ClientTier = "small" | "mid" | "large"

export type ReviewSource = "google" | "trustpilot" | "twitter"

export interface ClientMetrics {
  followersBefore: number
  followersAfter: number
  timeframeDays: number
  engagementMultiplier?: number
}

export interface ClientTestimonial {
  quote: string
  title?: string
  body?: string
  source?: ReviewSource
}

export interface ClientCaseStudy {
  headline: string
  excerpt: string
  about: string
  content: {
    intro: string
    challenge: string
    solution: string
    results: string
  }
}

export interface ClientStyle {
  bgColor: string
  nicheColor: string
}

export interface ClientPersona {
  id: string
  name: string
  handle: string
  avatar: string
  niche: ClientNiche
  tier: ClientTier
  featured: boolean
  hasCaseStudy: boolean
  seoSlug?: string // SEO-optimized URL slug for case studies
  metrics?: ClientMetrics
  testimonial?: ClientTestimonial
  caseStudy?: ClientCaseStudy
  style: ClientStyle
}

// ============================================================================
// CLIENTS DATA (45 personas)
// ============================================================================

export const CLIENTS: ClientPersona[] = [
  {
    id: "lifestyle-christina",
    name: "Christina Van",
    handle: "@christinavan",
    avatar: "/img/clients/lifestyle-christina.jpg",
    niche: "lifestyle",
    tier: "mid",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 8200,
      followersAfter: 87000,
      timeframeDays: 180,
      engagementMultiplier: 3.2,
    },
    testimonial: {
      quote: "Went from 8K to 87K in 6 months. My engagement rate tripled.",
      title: "Best investment I've made for my brand",
      body: "I was stuck at 8K followers for over a year, posting daily but barely growing. Within 6 months of using Outfame, I hit 87K and my engagement rate tripled. Brand deals started rolling in around the 50K mark. The AI targeting actually works—these are real people who engage with my content.",
      source: "google",
    },
    style: {
      bgColor: "rgb(255, 245, 240)",
      nicheColor: "bg-amber-100 text-amber-700",
    },
  },
  {
    id: "beauty-ashley",
    name: "Ashley Lardizabal",
    handle: "@ashleybeauty",
    avatar: "/img/clients/beauty-ashley.jpg",
    niche: "beauty",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 1200,
      followersAfter: 9800,
      timeframeDays: 120,
      engagementMultiplier: 2.8,
    },
    testimonial: {
      quote: "Hit 10K in 4 months. Finally got that swipe-up feature.",
      title: "Reached my first major milestone",
      body: "I'd been stuck under 2K for months, which meant no swipe-up links. Outfame got me to 10K in just 4 months by connecting me with people who actually care about beauty content. My tutorial views went from 200 to 3K+, and I landed my first paid partnership last month.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(245, 240, 255)",
      nicheColor: "bg-purple-100 text-purple-700",
    },
  },
  {
    id: "travel-ava",
    name: "Ava Miller",
    handle: "@aavamiller_",
    avatar: "/img/clients/travel-ava.jpg",
    niche: "travel",
    tier: "mid",
    featured: true,
    hasCaseStudy: true,
    seoSlug: "travel-instagram-144k-followers-case-study",
    metrics: {
      followersBefore: 12000,
      followersAfter: 156000,
      timeframeDays: 240,
      engagementMultiplier: 4.1,
    },
    testimonial: {
      quote: "156K followers and getting paid to travel. This changed my life.",
      title: "Turned my passion into a full-time career",
      body: "I quit my marketing job to travel full-time, but needed followers to make it sustainable. Outfame grew my account from 12K to 156K in 8 months. Now I'm getting paid to visit destinations I only dreamed about. The followers are real travelers who actually engage and ask questions.",
      source: "twitter",
    },
    style: {
      bgColor: "rgb(240, 250, 255)",
      nicheColor: "bg-sky-100 text-sky-700",
    },
    caseStudy: {
      headline: "How Ava grew from 12K to 156K and quit her job to travel",
      excerpt: "Marketing professional turned full-time travel creator after 13x growth in 8 months",
      about: "Ava was a marketing manager in Seattle who spent weekends exploring the Pacific Northwest, dreaming of traveling full-time but lacking the follower count to attract sponsorships.",
      content: {
        intro: "I'd been posting travel content for 2 years, but growth was painfully slow. At 12K followers, I was getting maybe 500 likes per post and zero brand interest. I knew I needed at least 50K to start landing paid partnerships, but at my growth rate, that would take 3+ years.",
        challenge: "The hardest part was the algorithm—my posts weren't reaching beyond my existing followers. I'd spend hours researching hashtags and posting at 'optimal times,' but nothing moved the needle. I was about to give up on the travel creator dream and just keep it as a weekend hobby.",
        solution: "Outfame's AI started targeting adventure travelers, photography enthusiasts, and people following destinations I posted about. Instead of random followers, I was connecting with people genuinely interested in travel content. I didn't have to change my content strategy—Outfame just put it in front of the right people.",
        results: "Hit 50K in 4 months and landed my first paid trip to Iceland. By month 8, I was at 156K with consistent partnership offers. My engagement rate jumped from 4% to 16%. I gave my notice at work in month 6 and I've been traveling full-time ever since. Last month I made more from Instagram than I did in my corporate job.",
      },
    },
  },
  {
    id: "travel-adele",
    name: "Adele Nadeau",
    handle: "@adele.nadeau",
    avatar: "/img/clients/travel-adele.jpg",
    niche: "food",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 1800,
      followersAfter: 11500,
      timeframeDays: 150,
      engagementMultiplier: 2.5,
    },
    testimonial: {
      quote: "Real followers who actually comment and share my recipes.",
      title: "Quality over quantity—these followers engage",
      body: "I was skeptical about growth services, but Outfame delivered real people, not bots. Went from 1.8K to 11.5K in 5 months, and my post engagement is better than ever. People actually ask where I sourced ingredients and share my recipes. That's what matters.",
      source: "google",
    },
    style: {
      bgColor: "rgb(255, 250, 240)",
      nicheColor: "bg-orange-100 text-orange-700",
    },
  },
  {
    id: "lifestyle-aleigh",
    name: "Aleigh Sharp",
    handle: "@aleigh_sharp",
    avatar: "/img/clients/lifestyle-allyson.jpg",
    niche: "fashion",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 2400,
      followersAfter: 14200,
      timeframeDays: 180,
      engagementMultiplier: 3.1,
    },
    testimonial: {
      quote: "Grew 5x in 6 months without changing my content strategy.",
      title: "Set it and forget it—actually works",
      body: "I didn't have time to engage with hundreds of accounts daily. Outfame handled all of that while I focused on creating content. My follower count went from 2.4K to 14K in 6 months, and the best part is these followers actually match my fashion aesthetic and vibe.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(250, 245, 255)",
      nicheColor: "bg-violet-100 text-violet-700",
    },
  },
  {
    id: "fashion-alena",
    name: "Alena Madison",
    handle: "@alenamadisonpop",
    avatar: "/img/clients/fashion-alena.jpg",
    niche: "fashion",
    tier: "mid",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 15000,
      followersAfter: 124000,
      timeframeDays: 210,
      engagementMultiplier: 3.8,
    },
    testimonial: {
      quote: "124K followers and working with major fashion brands.",
      title: "Landed dream partnerships with major brands",
      body: "Started at 15K and hit 124K in 7 months. Now I'm working with Revolve, Princess Polly, and brands I used to just tag hoping they'd notice. Outfame's targeting found people who actually care about fashion, not just random followers. My Story views went from 2K to 40K+.",
      source: "google",
    },
    style: {
      bgColor: "rgb(255, 240, 245)",
      nicheColor: "bg-pink-100 text-pink-700",
    },
  },
  {
    id: "beauty-alexa",
    name: "Alexa Kim",
    handle: "@alexakim11",
    avatar: "/img/clients/beauty-alexa.jpg",
    niche: "beauty",
    tier: "mid",
    featured: true,
    hasCaseStudy: true,
    seoSlug: "beauty-instagram-85k-followers-case-study",
    metrics: {
      followersBefore: 6800,
      followersAfter: 92000,
      timeframeDays: 200,
      engagementMultiplier: 5.2,
    },
    testimonial: {
      quote: "6.8K to 92K in under 7 months. Makeup brands now reach out to me.",
      title: "Went from sending PR requests to receiving them",
      body: "I used to email brands begging for PR packages. Now they find me. Outfame grew my beauty account from 6.8K to 92K in about 7 months, and my engagement rate is insane—makeup tutorials regularly hit 50K+ views. The AI found my exact target audience: people who love K-beauty and skincare.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(255, 245, 250)",
      nicheColor: "bg-rose-100 text-rose-700",
    },
    caseStudy: {
      headline: "How Alexa went from 6.8K to 92K and turned PR requests into PR packages",
      excerpt: "Beauty creator built a thriving influencer career with 13x follower growth in 7 months",
      about: "Alexa was a beauty enthusiast posting makeup tutorials from her apartment, frustrated by slow growth despite consistent, high-quality content that took hours to produce.",
      content: {
        intro: "I was spending 3-4 hours on each makeup tutorial—planning, filming, editing—and getting maybe 800 views. At 6.8K followers, brands wouldn't even respond to my emails. I'd see creators with similar content but 50K+ followers getting sent free products and paid partnerships, and I couldn't figure out what I was doing wrong.",
        challenge: "My content quality was there, but nobody saw it. The algorithm kept showing my posts to the same 1,000 people. I tried every growth hack—engagement pods, follow/unfollow, buying shoutouts—but nothing gave me real, lasting growth. I was ready to quit and just keep it as a hobby.",
        solution: "Outfame's AI analyzed my content and started connecting me with K-beauty enthusiasts, skincare lovers, and people following similar makeup artists. Within the first month, I noticed my tutorial views doubling. The followers weren't just numbers—they were people genuinely interested in the products I was using and techniques I was teaching.",
        results: "Hit 30K in 3 months and got my first PR package from a Korean skincare brand. By month 7, I was at 92K with consistent brand partnerships from Sephora, Fenty, and Charlotte Tilbury. My engagement rate went from 12% to 62%—tutorials now regularly get 50K+ views. I quit my retail job last month and I'm doing beauty content full-time.",
      },
    },
  },
  {
    id: "lifestyle-allyson",
    name: "Allyson Martinez",
    handle: "@allysonn.m",
    avatar: "/img/clients/lifestyle-aleigh.jpg",
    niche: "beauty",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 1500,
      followersAfter: 8900,
      timeframeDays: 90,
      engagementMultiplier: 2.9,
    },
    testimonial: {
      quote: "Almost 9K followers in 3 months. Didn't think growth could be this fast.",
      title: "Fastest growth I've ever experienced",
      body: "Jumped from 1.5K to nearly 9K in just 3 months. I've tried other services before and got nothing but fake accounts. Outfame delivered real people who like, comment, and actually watch my beauty tutorials. My content hasn't changed—I just finally have the right audience seeing it.",
      source: "google",
    },
    style: {
      bgColor: "rgb(245, 255, 250)",
      nicheColor: "bg-emerald-100 text-emerald-700",
    },
  },
  {
    id: "fitness-alyssa",
    name: "Alyssa McPhetridge",
    handle: "@alyssa.mcphetridge",
    avatar: "/img/clients/fitness-alyssa.jpg",
    niche: "fitness",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 2100,
      followersAfter: 13800,
      timeframeDays: 165,
      engagementMultiplier: 3.4,
    },
    testimonial: {
      quote: "13.8K fitness enthusiasts following my journey. DMs went from 5 to 50+ daily.",
      title: "Built a real community of fitness lovers",
      body: "I'm a personal trainer who was barely reaching anyone online. Outfame connected me with actual fitness enthusiasts—people who ask about my workout splits and nutrition plans. Went from 2.1K to 13.8K in about 5 months. Now I'm booking online clients weekly through Instagram.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(240, 255, 245)",
      nicheColor: "bg-green-100 text-green-700",
    },
  },
  {
    id: "travel-andi",
    name: "Andi Martinez",
    handle: "@andiiiiam",
    avatar: "/img/clients/travel-andi.jpg",
    niche: "music",
    tier: "mid",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 9500,
      followersAfter: 118000,
      timeframeDays: 220,
      engagementMultiplier: 4.3,
    },
    testimonial: {
      quote: "118K followers and record labels are reaching out now.",
      title: "Music influencer status unlocked",
      body: "Went from 9.5K to 118K in about 7 months. The crazy part? Record labels and event promoters now reach out offering gigs and collaboration deals. I made back what I spent on Outfame in the first month just from one show. These followers actually care about music—my releases get thousands of streams within hours.",
      source: "twitter",
    },
    style: {
      bgColor: "rgb(255, 250, 245)",
      nicheColor: "bg-amber-100 text-amber-700",
    },
  },
  {
    id: "lifestyle-arielle",
    name: "Arielle Jallen",
    handle: "@ariellejallen",
    avatar: "/img/clients/lifestyle-arielle.jpg",
    niche: "lifestyle",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 1900,
      followersAfter: 12400,
      timeframeDays: 135,
      engagementMultiplier: 2.7,
    },
    testimonial: {
      quote: "12.4K in 4.5 months. My Reels finally started going viral.",
      title: "Algorithm boost from real engagement",
      body: "My Reels were flopping with barely 300 views. After growing from 1.9K to 12.4K with Outfame, my Reels started hitting 20K-50K views regularly. The algorithm rewards engagement, and these real followers actually engage. I've had 3 Reels go over 100K views in the past month.",
      source: "google",
    },
    style: {
      bgColor: "rgb(250, 245, 240)",
      nicheColor: "bg-orange-100 text-orange-700",
    },
  },
  {
    id: "beauty-aspen",
    name: "Aspen Hayes",
    handle: "@aspenbeauty",
    avatar: "/img/clients/beauty-aspen.jpg",
    niche: "beauty",
    tier: "mid",
    featured: true,
    hasCaseStudy: true,
    seoSlug: "beauty-instagram-117k-followers-case-study",
    metrics: {
      followersBefore: 8500,
      followersAfter: 125000,
      timeframeDays: 180,
      engagementMultiplier: 3.2,
    },
    testimonial: {
      quote: "From 8K to 125K in 6 months. My DMs are flooded with brand deals now.",
      title: "Finally landed my dream brand partnerships",
      body: "I was stuck at 8K followers for over a year, posting daily but getting nowhere. Outfame's AI found my exact audience—people who actually engage with my beauty content. Within 6 months I hit 125K and started getting paid partnership offers from brands I've dreamed of working with.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(252, 248, 243)",
      nicheColor: "bg-pink-100 text-pink-700",
    },
    caseStudy: {
      headline: "How Aspen Hayes grew from 8K to 125K in 6 months",
      excerpt: "Beauty creator breaks through growth plateau and lands dream brand partnerships",
      about: "Aspen is a beauty content creator specializing in clean beauty and skincare routines. Based in California, she focuses on honest reviews and natural makeup looks.",
      content: {
        intro: "After a year of posting daily beauty content, Aspen was stuck at 8,500 followers with minimal engagement. She was spending 3-4 hours daily on Instagram but seeing no growth. Brand partnerships were scarce, and she was considering giving up on her creator dreams.",
        challenge: "Aspen's content was high-quality, but she wasn't reaching the right audience. Her posts got buried in hashtags flooded with millions of posts. Manual engagement was time-consuming and ineffective—she'd spend hours liking and commenting, only to gain a few followers who rarely engaged back. She needed a way to find people genuinely interested in clean beauty.",
        solution: "Outfame's AI analyzed her top-performing posts and identified her ideal audience: women aged 22-35 interested in clean beauty, skincare, and sustainable products. The system automatically engaged with this precise demographic 24/7, targeting followers of similar creators and relevant hashtag communities. Aspen focused on creating content while Outfame handled audience building.",
        results: "In 6 months, Aspen grew from 8,500 to 125,000 followers with a 3.2x increase in engagement rate. She now receives 15-20 brand partnership inquiries monthly, including deals with major clean beauty brands. Her average post reaches 45K people organically, and she's earning $8K-12K per month from sponsored content.",
      },
    },
  },
  {
    id: "fitness-avamor",
    name: "Ava Morrison",
    handle: "@ava.fit",
    avatar: "/img/clients/fitness-avamor.jpg",
    niche: "fitness",
    tier: "mid",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 12000,
      followersAfter: 89000,
      timeframeDays: 150,
      engagementMultiplier: 2.8,
    },
    testimonial: {
      quote: "89K followers in 5 months. My online coaching program sold out in 48 hours.",
      title: "Scaled my fitness coaching business 10x",
      body: "I had great content but couldn't break past 12K followers. Outfame found my ideal audience—women serious about strength training. My engagement tripled, and when I launched my coaching program, it sold out in 48 hours. This completely changed my business.",
      source: "google",
    },
    style: {
      bgColor: "rgb(243, 250, 248)",
      nicheColor: "bg-green-100 text-green-700",
    },
  },
  {
    id: "travel-averi",
    name: "Averi Madison",
    handle: "@averi.travels",
    avatar: "/img/clients/travel-averi.jpg",
    niche: "food",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 1200,
      followersAfter: 9800,
      timeframeDays: 90,
      engagementMultiplier: 4.1,
    },
    testimonial: {
      quote: "Grew to nearly 10K in 3 months. Restaurants are now reaching out to me for collaborations.",
      title: "From hobby to paid food partnerships",
      body: "I started my food account just for fun, but Outfame helped me turn it into something real. In 3 months I went from 1,200 to nearly 10K followers. Now local restaurants and food brands reach out offering free meals in exchange for content. It's incredible.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(254, 249, 243)",
      nicheColor: "bg-orange-100 text-orange-700",
    },
  },
  {
    id: "wellness-caitlin",
    name: "Caitlin Warren",
    handle: "@caitlin_wellness",
    avatar: "/img/clients/wellness-caitlin.jpg",
    niche: "wellness",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 850,
      followersAfter: 7200,
      timeframeDays: 120,
      engagementMultiplier: 3.6,
    },
    testimonial: {
      quote: "My wellness community grew 8x in 4 months. Finally found people who truly care about mindful living.",
      title: "Built an engaged wellness community",
      body: "I was sharing meditation and wellness tips to basically no one. Outfame connected me with people who actually care about mindful living. My community grew from 850 to 7,200 genuinely engaged followers. The conversations in my DMs are so meaningful now.",
      source: "google",
    },
    style: {
      bgColor: "rgb(248, 250, 252)",
      nicheColor: "bg-blue-100 text-blue-700",
    },
  },
  {
    id: "lifestyle-catherinegordon",
    name: "Catherine Gordon",
    handle: "@catherine.lifestyle",
    avatar: "/img/clients/lifestyle-catherinegordon.jpg",
    niche: "lifestyle",
    tier: "mid",
    featured: true,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 15000,
      followersAfter: 118000,
      timeframeDays: 210,
      engagementMultiplier: 2.9,
    },
    testimonial: {
      quote: "118K followers and brands pay me $3K per post now. Worth every penny.",
      title: "Turned my lifestyle content into full-time income",
      body: "I was posting consistently but stuck at 15K followers. Outfame's targeting found my exact audience—young professionals interested in affordable luxury. In 7 months I grew to 118K and now earn $3-5K per sponsored post. This is my full-time income now.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(250, 248, 246)",
      nicheColor: "bg-amber-100 text-amber-700",
    },
  },
  {
    id: "business-catherineromero",
    name: "Catherine Romero",
    handle: "@c.romero",
    avatar: "/img/clients/business-catherineromero.jpg",
    niche: "business",
    tier: "mid",
    featured: true,
    hasCaseStudy: true,
    seoSlug: "business-instagram-86k-followers-case-study",
    metrics: {
      followersBefore: 6200,
      followersAfter: 92000,
      timeframeDays: 240,
      engagementMultiplier: 3.8,
    },
    testimonial: {
      quote: "Grew to 92K and landed a TEDx talk. My consulting inquiries increased 500%.",
      title: "Became a recognized voice in entrepreneurship",
      body: "As a business consultant, I needed credibility and reach. Outfame helped me grow from 6K to 92K followers—all entrepreneurs and business owners. My consulting inquiries went from 2-3 per month to 10-15 per week. I even got invited to speak at TEDx.",
      source: "google",
    },
    style: {
      bgColor: "rgb(249, 247, 253)",
      nicheColor: "bg-purple-100 text-purple-700",
    },
    caseStudy: {
      headline: "How Catherine Romero grew to 92K and became a recognized business voice",
      excerpt: "Business consultant transforms Instagram into lead generation machine and lands TEDx",
      about: "Catherine is a business consultant and entrepreneur coach helping women launch and scale online businesses. She focuses on sustainable growth strategies and authentic marketing.",
      content: {
        intro: "Catherine had valuable business insights to share but only 6,200 followers, mostly friends and family. As a consultant, she needed social proof to attract high-ticket clients. Her posts got 50-100 likes, and she received maybe 2-3 consulting inquiries per month—not enough to sustain her business.",
        challenge: "Catherine's expertise wasn't the problem—her reach was. She needed to connect with entrepreneurs and business owners who could benefit from her consulting services. Manual networking on Instagram was inefficient, and her content wasn't reaching decision-makers. She needed a way to build authority and attract qualified leads consistently.",
        solution: "Outfame's AI identified her ideal audience: female entrepreneurs, startup founders, and business owners aged 25-45. The system engaged with this specific demographic across business hashtags, entrepreneur communities, and competitors' audiences. Catherine maintained her content calendar while Outfame worked 24/7 to build her reach among her target market.",
        results: "Over 8 months, Catherine grew from 6,200 to 92,000 highly targeted followers. Her consulting inquiries skyrocketed from 2-3 per month to 10-15 per week, allowing her to be selective with clients. She increased her rates from $2K to $8K per client and still has a waitlist. The social proof led to a TEDx speaking invitation and multiple podcast features.",
      },
    },
  },
  {
    id: "photography-taylor",
    name: "Taylor Monroe",
    handle: "@taymonte",
    avatar: "/img/clients/photography-taylor.jpg",
    niche: "photography",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 1500,
      followersAfter: 11200,
      timeframeDays: 150,
      engagementMultiplier: 3.4,
    },
    testimonial: {
      quote: "11K followers and booking weddings at $4K each. My photography business is thriving.",
      title: "Transformed my photography into a real business",
      body: "I'm a photographer but couldn't get clients through Instagram. Outfame connected me with engaged couples and people planning events in my area. Went from 1,500 to 11K followers in 5 months. Now I book 2-3 weddings per month at $4K each.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(252, 245, 243)",
      nicheColor: "bg-rose-100 text-rose-700",
    },
  },
  {
    id: "fashion-tessa",
    name: "Tessa Brandt",
    handle: "@tessabrandt",
    avatar: "/img/clients/fashion-tessa.jpg",
    niche: "fashion",
    tier: "mid",
    featured: true,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 9500,
      followersAfter: 156000,
      timeframeDays: 270,
      engagementMultiplier: 4.2,
    },
    testimonial: {
      quote: "156K followers in 9 months. Fashion brands I used to dream about now send me free pieces.",
      title: "Living my fashion influencer dreams",
      body: "I love fashion but was stuck under 10K for years. Outfame found my people—women who love affordable luxury fashion. Grew to 156K in 9 months. Brands like Revolve and Princess Polly now send me free clothes and pay me for posts. It's surreal.",
      source: "google",
    },
    style: {
      bgColor: "rgb(245, 243, 250)",
      nicheColor: "bg-indigo-100 text-indigo-700",
    },
  },
  {
    id: "lifestyle-tegan",
    name: "Tegan Nash",
    handle: "@tega.nn",
    avatar: "/img/clients/lifestyle-tegan.jpg",
    niche: "food",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 680,
      followersAfter: 6400,
      timeframeDays: 105,
      engagementMultiplier: 5.2,
    },
    testimonial: {
      quote: "Grew from 680 to 6.4K in 3.5 months. My engagement rate is insane compared to before.",
      title: "Finally found my tribe on Instagram",
      body: "I was posting recipes into the void with under 700 followers. Outfame found people who actually vibe with my cooking content—my engagement rate went up 5x. Grew to 6,400 followers in just over 3 months and now food brands are starting to notice me.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(250, 247, 244)",
      nicheColor: "bg-amber-100 text-amber-700",
    },
  },
  {
    id: "travel-teganhart",
    name: "Tegan Hart",
    handle: "@tegan.hart",
    avatar: "/img/clients/travel-teganhart.jpg",
    niche: "food",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 920,
      followersAfter: 8100,
      timeframeDays: 135,
      engagementMultiplier: 3.9,
    },
    testimonial: {
      quote: "8K followers and food brands are reaching out. My content finally gets seen.",
      title: "Turned food passion into real opportunities",
      body: "My recipe videos were getting 30 likes max. Outfame connected me with people who love cooking and trying new restaurants. Grew from 920 to 8,100 followers in 4.5 months. Now food brands actually respond to my emails.",
      source: "google",
    },
    style: {
      bgColor: "rgb(243, 251, 255)",
      nicheColor: "bg-cyan-100 text-cyan-700",
    },
  },
  {
    id: "lifestyle-sophie",
    name: "Sophie Laurent",
    handle: "@sophielaurent",
    avatar: "/img/clients/lifestyle-sophie.jpg",
    niche: "beauty",
    tier: "mid",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 18500,
      followersAfter: 142000,
      timeframeDays: 195,
      engagementMultiplier: 2.7,
    },
    testimonial: {
      quote: "142K followers in 6.5 months. Beauty affiliate income alone covers my rent now.",
      title: "Built a profitable beauty brand",
      body: "I had decent content but couldn't crack 20K followers. Outfame's targeting was surgical—found people obsessed with skincare and beauty products. Hit 142K in under 7 months. My Amazon affiliate and LTK beauty income went from $200/month to $3,500/month.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(254, 252, 248)",
      nicheColor: "bg-yellow-100 text-yellow-700",
    },
  },
  {
    id: "travel-marcus",
    name: "Marcus Williams",
    handle: "@marcus.explores",
    avatar: "/img/clients/travel-marcus.jpg",
    niche: "fashion",
    tier: "mid",
    featured: true,
    hasCaseStudy: true,
    seoSlug: "fashion-instagram-119k-followers-case-study",
    metrics: {
      followersBefore: 8500,
      followersAfter: 127000,
      timeframeDays: 180,
      engagementMultiplier: 3.2,
    },
    testimonial: {
      quote: "Went from 8k to 127k in 6 months. My brand deals tripled.",
      title: "Best investment I made for my fashion brand",
      body: "I was stuck at 8k followers for over a year, posting consistently but getting nowhere. Outfame's AI found my exact audience—people who actually engage with streetwear and menswear content. Now I'm landing brand deals with fashion labels and sneaker companies I never thought would notice me.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(230, 242, 255)",
      nicheColor: "bg-blue-100 text-blue-700",
    },
    caseStudy: {
      headline: "How Marcus grew from 8.5k to 127k in 6 months",
      excerpt: "Fashion creator goes from stagnant growth to landing major brand deals with streetwear brands.",
      about: "Marcus is a full-time fashion content creator specializing in streetwear, sneaker culture, and menswear styling across urban destinations.",
      content: {
        intro: "After two years of consistent posting, Marcus hit a wall at 8,500 followers. His engagement was decent but growth had completely stalled. He was spending 3-4 hours daily on manual engagement with minimal results.",
        challenge: "Marcus's biggest frustration was reaching the wrong audience. His followers weren't engaging because they weren't genuinely interested in streetwear fashion—they were random accounts from follow-for-follow tactics. He needed real fashion enthusiasts who would actually engage with his content and attract brand partnerships.",
        solution: "Outfame's AI analyzed Marcus's top-performing posts and identified his ideal audience: streetwear enthusiasts aged 18-35 interested in sneaker culture and urban fashion. The system automatically engaged with users following similar creators and fashion hashtags. Within the first month, Marcus saw a 340% increase in story replies from genuinely interested followers.",
        results: "In 6 months, Marcus grew to 127,000 followers with 3.2x higher engagement rates. More importantly, he landed partnerships with StockX, several streetwear brands, and a sneaker collaboration worth $15k. His DMs are now flooded with collaboration requests instead of spam.",
      },
    },
  },
  {
    id: "lifestyle-max",
    name: "Max van der Berg",
    handle: "@maxvdberg",
    avatar: "/img/clients/lifestyle-max.jpg",
    niche: "lifestyle",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 1200,
      followersAfter: 9400,
      timeframeDays: 120,
      engagementMultiplier: 2.8,
    },
    testimonial: {
      quote: "Finally cracked 10k and got verified. Worth every penny.",
      title: "Got me to 10k in under 4 months",
      body: "I tried three other growth services before Outfame and they all sent bot accounts. Outfame delivered real followers who actually watch my stories and comment. Hit 10k in 4 months and finally got that verified badge.",
      source: "google",
    },
    style: {
      bgColor: "rgb(255, 247, 237)",
      nicheColor: "bg-orange-100 text-orange-700",
    },
  },
  {
    id: "art-diego",
    name: "Diego Morales",
    handle: "@d.morales.art",
    avatar: "/img/clients/art-diego.jpg",
    niche: "art",
    tier: "small",
    featured: true,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 850,
      followersAfter: 7200,
      timeframeDays: 90,
      engagementMultiplier: 4.1,
    },
    testimonial: {
      quote: "My art is finally reaching people who actually appreciate it.",
      title: "Real collectors, not just random followers",
      body: "As a digital artist, I needed followers who would actually buy my work. Outfame connected me with art collectors and enthusiasts. I've sold 12 pieces in the last 2 months and my engagement is through the roof.",
      source: "twitter",
    },
    style: {
      bgColor: "rgb(243, 232, 255)",
      nicheColor: "bg-purple-100 text-purple-700",
    },
  },
  {
    id: "travel-adrian",
    name: "Adrian Fischer",
    handle: "@adrianfisch",
    avatar: "/img/clients/travel-adrian.jpg",
    niche: "business",
    tier: "mid",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 12000,
      followersAfter: 89000,
      timeframeDays: 210,
      engagementMultiplier: 2.4,
    },
    testimonial: {
      quote: "Scaled from 12k to 89k while I focused on creating content.",
      title: "Set it and forget it actually works",
      body: "I was skeptical about automation but Outfame proved me wrong. The AI handles all the engagement while I focus on business strategy content. Growth has been steady and sustainable—no weird spikes or drops. My consulting pipeline is now completely full from Instagram leads.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(224, 247, 250)",
      nicheColor: "bg-cyan-100 text-cyan-700",
    },
  },
  {
    id: "business-alvaro",
    name: "Alvaro Klein",
    handle: "@alvaroklein",
    avatar: "/img/clients/business-alvaro.jpg",
    niche: "business",
    tier: "mid",
    featured: true,
    hasCaseStudy: true,
    seoSlug: "business-instagram-88k-followers-case-study",
    metrics: {
      followersBefore: 5400,
      followersAfter: 94000,
      timeframeDays: 240,
      engagementMultiplier: 3.7,
    },
    testimonial: {
      quote: "Grew my consulting business to 6 figures thanks to my Instagram growth.",
      title: "Instagram became my #1 client acquisition channel",
      body: "I went from posting into the void to having potential clients reaching out daily. Outfame helped me build an audience of actual entrepreneurs and business owners who need my services. My Instagram now generates more revenue than my website.",
      source: "google",
    },
    style: {
      bgColor: "rgb(236, 253, 245)",
      nicheColor: "bg-green-100 text-green-700",
    },
    caseStudy: {
      headline: "How Alvaro built a 6-figure consulting business from Instagram",
      excerpt: "Business consultant turns Instagram into his primary client acquisition channel, scaling from 5k to 94k followers.",
      about: "Alvaro is a business strategy consultant helping early-stage startups with growth frameworks and go-to-market strategies.",
      content: {
        intro: "Alvaro had the expertise but struggled to find clients. With 5,400 followers, his content was solid but wasn't reaching decision-makers. He was spending hours networking on LinkedIn with mediocre results and knew Instagram could be more effective.",
        challenge: "Most growth tactics attracted the wrong audience—aspiring entrepreneurs with no budget, not established business owners who could afford consulting fees. Alvaro needed followers who were serious about scaling their businesses and had the resources to invest in expert guidance.",
        solution: "Outfame's targeting focused on founders, startup employees, and business owners following top business influencers. The AI engaged with people commenting on business strategy content and attending virtual startup events. Within 60 days, Alvaro noticed his followers were asking more sophisticated questions in his DMs.",
        results: "In 8 months, Alvaro grew to 94,000 followers and converted Instagram into his primary lead source. He now books 4-6 consulting calls per week directly from Instagram, averaging $8k per client. His content reaches business owners who actually implement his advice and refer him to their networks.",
      },
    },
  },
  {
    id: "photography-arnau",
    name: "Arnau Camarena",
    handle: "@arnau_shoots",
    avatar: "/img/clients/photography-arnau.jpg",
    niche: "photography",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 1800,
      followersAfter: 11500,
      timeframeDays: 150,
      engagementMultiplier: 3.4,
    },
    testimonial: {
      quote: "Booked 8 wedding clients in 3 months from Instagram alone.",
      title: "My wedding photography business took off",
      body: "I was relying on referrals and barely scraping by. After using Outfame, my follower count exploded with engaged couples actually looking for photographers. I'm now booking weddings 6 months out and raised my prices by 40%.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(254, 242, 242)",
      nicheColor: "bg-red-100 text-red-700",
    },
  },
  {
    id: "fashion-bart",
    name: "Bart Vandenberg",
    handle: "@bart.graph",
    avatar: "/img/clients/fashion-bart.jpg",
    niche: "fashion",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 2100,
      followersAfter: 13800,
      timeframeDays: 135,
      engagementMultiplier: 2.9,
    },
    testimonial: {
      quote: "Streetwear brands started sending me free clothes after hitting 10k.",
      title: "Finally getting recognized in the fashion scene",
      body: "Growing my fashion account was impossible until I found Outfame. The AI targets people who actually care about streetwear, not random accounts. I've received PR packages from 5 brands and landed my first paid partnership.",
      source: "twitter",
    },
    style: {
      bgColor: "rgb(249, 250, 251)",
      nicheColor: "bg-slate-100 text-slate-700",
    },
  },
  {
    id: "tech-berkay",
    name: "Berkay Guler",
    handle: "@berkay_tech",
    avatar: "/img/clients/tech-berkay.jpg",
    niche: "tech",
    tier: "mid",
    featured: true,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 6700,
      followersAfter: 78000,
      timeframeDays: 195,
      engagementMultiplier: 3.5,
    },
    testimonial: {
      quote: "My tech reviews now reach 78k people. Brands are paying me for coverage.",
      title: "Turned my tech hobby into a real income stream",
      body: "I was reviewing gadgets for fun with barely any followers. Outfame helped me build an audience of actual tech enthusiasts who value my opinions. Now I'm getting review units sent directly from brands and making $3-5k per sponsored post.",
      source: "google",
    },
    style: {
      bgColor: "rgb(240, 255, 245)",
      nicheColor: "bg-emerald-100 text-emerald-700",
    },
  },
  {
    id: "fitness-bram",
    name: "Bram Gabrielse",
    handle: "@bramgabrielse",
    avatar: "/img/clients/fitness-bram.jpg",
    niche: "fitness",
    tier: "mid",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 4200,
      followersAfter: 67000,
      timeframeDays: 165,
      engagementMultiplier: 4.2,
    },
    testimonial: {
      quote: "Built a 6-figure fitness coaching business in under a year.",
      title: "From struggling trainer to fully booked coach",
      body: "I went from training 3 clients at a local gym to running a full online coaching program with 45 clients. Outfame filled my pipeline with people genuinely interested in fitness transformations. My program is now consistently sold out.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(254, 249, 195)",
      nicheColor: "bg-yellow-100 text-yellow-700",
    },
  },
  {
    id: "lifestyle-chris",
    name: "Chris DeLeon",
    handle: "@chris_deleon",
    avatar: "/img/clients/lifestyle-chris.jpg",
    niche: "lifestyle",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 980,
      followersAfter: 8200,
      timeframeDays: 105,
      engagementMultiplier: 3.1,
    },
    testimonial: {
      quote: "Gained 7k real followers in 3 months without lifting a finger.",
      title: "Most effortless growth I've ever experienced",
      body: "I set up Outfame, forgot about it for 3 months, and came back to 8k followers with insane engagement. People are actually messaging me asking about my lifestyle tips and where I shop. This actually works.",
      source: "twitter",
    },
    style: {
      bgColor: "rgb(252, 231, 243)",
      nicheColor: "bg-pink-100 text-pink-700",
    },
  },
  {
    id: "travel-conrad",
    name: "Conrad Mitchell",
    handle: "@conrad.ventures",
    avatar: "/img/clients/travel-conrad.jpg",
    niche: "fashion",
    tier: "mid",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 15000,
      followersAfter: 156000,
      timeframeDays: 270,
      engagementMultiplier: 2.7,
    },
    testimonial: {
      quote: "140k new followers in 9 months. Now working with major fashion brands.",
      title: "Became a full-time fashion influencer",
      body: "I quit my corporate job to pursue fashion full-time and needed to monetize fast. Outfame accelerated my growth so quickly that I started getting paid partnership offers within 4 months. Now I work with fashion brands across 3 countries.",
      source: "google",
    },
    style: {
      bgColor: "rgb(237, 233, 254)",
      nicheColor: "bg-violet-100 text-violet-700",
    },
  },
  {
    id: "lifestyle-daan",
    name: "Daan Welsch",
    handle: "@daanwelsch",
    avatar: "/img/clients/lifestyle-daan.jpg",
    niche: "lifestyle",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 1500,
      followersAfter: 10200,
      timeframeDays: 120,
      engagementMultiplier: 2.6,
    },
    testimonial: {
      quote: "Hit 10k and unlocked swipe-up links. Game changer for my brand.",
      title: "Finally monetizing my lifestyle content",
      body: "Getting to 10k felt impossible until I used Outfame. Now I can add links to my stories and I'm making affiliate income from my recommendations. The followers are real people who actually click through and buy.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(255, 251, 235)",
      nicheColor: "bg-amber-100 text-amber-700",
    },
  },
  {
    id: "lifestyle-lucas",
    name: "Lucas Rivera",
    handle: "@lucas.coast",
    avatar: "/img/clients/lifestyle-lucas.jpg",
    niche: "travel",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 1200,
      followersAfter: 8500,
      timeframeDays: 90,
      engagementMultiplier: 2.4,
    },
    testimonial: {
      quote: "Went from posting to empty beaches to having 8,500 people who actually engage with my content.",
      title: "Finally found my audience after 2 years of struggling",
      body: "I'd been posting surf and coastal travel content for two years with barely any growth. Outfame found the exact people who care about beach destinations and ocean conservation. My engagement rate doubled and I landed my first tourism board partnership within 60 days.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(191, 219, 254)",
      nicheColor: "bg-blue-100 text-blue-700",
    },
  },
  {
    id: "photography-daniel",
    name: "Daniel Cross",
    handle: "@dcross.visuals",
    avatar: "/img/clients/photography-daniel.jpg",
    niche: "photography",
    tier: "mid",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 1200,
      followersAfter: 8500,
      timeframeDays: 120,
      engagementMultiplier: 3.1,
    },
    testimonial: {
      quote: "From 8K to 67K in six months. Now shooting from clients I only dreamed about.",
      title: "Turned my photography side hustle into full-time income",
      body: "I was stuck at 8K followers for over a year, posting consistently but getting nowhere. Outfame's AI targeted photography enthusiasts and potential clients in my city. Within three months I had enough inquiries to quit my day job.",
      source: "google",
    },
    style: {
      bgColor: "rgb(229, 231, 235)",
      nicheColor: "bg-gray-100 text-gray-700",
    },
  },
  {
    id: "travel-alex",
    name: "Alex Morrison",
    handle: "@alexplores",
    avatar: "/img/clients/travel-alex.jpg",
    niche: "travel",
    tier: "mid",
    featured: true,
    hasCaseStudy: true,
    seoSlug: "travel-instagram-133k-followers-case-study",
    metrics: {
      followersBefore: 12000,
      followersAfter: 145000,
      timeframeDays: 240,
      engagementMultiplier: 2.8,
    },
    testimonial: {
      quote: "12K to 145K followers while I was literally traveling the world. This thing runs itself.",
      title: "Set it up once, grew to 145K while backpacking",
      body: "I needed something that worked while I was off-grid in remote locations. Outfame grew my account from 12K to 145K over eight months without me touching it. Now tourism boards reach out to me for partnerships.",
      source: "trustpilot",
    },
    style: {
      bgColor: "rgb(254, 215, 170)",
      nicheColor: "bg-orange-100 text-orange-700",
    },
    caseStudy: {
      headline: "How Alex grew from 12K to 145K in 8 months while traveling",
      excerpt: "Adventure photographer went from struggling creator to landing tourism board partnerships by letting Outfame run on autopilot.",
      about: "Alex is an adventure photographer who documents remote destinations and sustainable travel. Before Outfame, managing social media while traveling was impossible.",
      content: {
        intro: "Alex was stuck at 12,000 followers despite posting stunning travel photography from around the world. The challenge wasn't content quality—it was consistency. Spending days off-grid meant zero engagement, and the algorithm punished him for it. He needed growth that didn't require constant presence.",
        challenge: "Traveling to remote locations meant Alex was offline for weeks at a time. By the time he reconnected, his posts had lost momentum and the engagement window had closed. He tried scheduling tools, but they couldn't interact with his audience or find new followers. His growth had flatlined and brand opportunities were few.",
        solution: "Outfame's AI analyzed Alex's best-performing adventure content and identified travel enthusiasts, outdoor brands, and eco-conscious audiences. The system engaged with these users 24/7—liking relevant posts, following adventure accounts, and interacting authentically while Alex was trekking in Nepal or diving in Indonesia. He set it up once and let it run.",
        results: "In eight months, Alex grew from 12,000 to 145,000 followers. His engagement rate jumped from 1.8% to 5.2%. Tourism boards started reaching out for paid partnerships, and outdoor gear brands offered sponsorships. He now earns more from Instagram than from selling prints, all while spending most of his time off the grid doing what he loves.",
      },
    },
  },
  {
    id: "art-dario",
    name: "Dario Santini",
    handle: "@dario.creates",
    avatar: "/img/clients/art-dario.jpg",
    niche: "art",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 800,
      followersAfter: 6200,
      timeframeDays: 75,
      engagementMultiplier: 3.5,
    },
    testimonial: {
      quote: "Sold three commissions in my first month after setting this up. Finally connecting with real art collectors.",
      title: "From invisible artist to selling commissions consistently",
      body: "I was posting my digital art into the void with maybe 20 likes per post. Outfame found people who actually buy art, not just scroll past it. My DMs went from empty to daily commission requests within 45 days.",
      source: "google",
    },
    style: {
      bgColor: "rgb(233, 213, 255)",
      nicheColor: "bg-purple-100 text-purple-700",
    },
  },
  {
    id: "fitness-josh",
    name: "Josh Allen",
    handle: "@josh_fit_allen",
    avatar: "/img/clients/fitness-josh.jpg",
    niche: "fitness",
    tier: "mid",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 14000,
      followersAfter: 156000,
      timeframeDays: 300,
      engagementMultiplier: 2.5,
    },
    testimonial: {
      quote: "14K to 156K in ten months. My fitness app went from 50 to 2,400 paying users.",
      title: "Built a six-figure fitness business from Instagram growth",
      body: "I had the app and the programs, but no one knew I existed. Outfame found people actually interested in calisthenics and bodyweight training. My app downloads exploded and I'm now running a legitimate business with recurring revenue.",
      source: "twitter",
    },
    style: {
      bgColor: "rgb(220, 252, 231)",
      nicheColor: "bg-green-100 text-green-700",
    },
  },
  {
    id: "travel-fitzgerald",
    name: "Fitzgerald Cole",
    handle: "@fitz.wanderlust",
    avatar: "/img/clients/travel-fitzgerald.jpg",
    niche: "food",
    tier: "small",
    featured: false,
    hasCaseStudy: false,
    metrics: {
      followersBefore: 920,
      followersAfter: 8100,
      timeframeDays: 135,
      engagementMultiplier: 3.9,
    },
    testimonial: {
      quote: "8K followers and food brands are reaching out. My content finally gets seen.",
      title: "Turned food passion into real opportunities",
      body: "My recipe videos were getting 30 likes max. Outfame connected me with people who love cooking and trying new restaurants. Grew from 920 to 8,100 followers in 4.5 months. Now food brands actually respond to my emails.",
      source: "google",
    },
    style: {
      bgColor: "rgb(243, 251, 255)",
      nicheColor: "bg-cyan-100 text-cyan-700",
    },
  },
]

// ============================================================================
// HELPER FUNCTIONS (tree-shakeable exports)
// ============================================================================

/** Get all featured clients for hero sections - interleaved for gender diversity */
export const getFeaturedClients = () => {
  const featured = CLIENTS.filter((c) => c.featured)
  // Male client IDs for diversity interleaving
  const maleIds = new Set(["travel-marcus", "art-diego", "business-alvaro", "tech-berkay", "travel-alex"])
  const males = featured.filter((c) => maleIds.has(c.id))
  const females = featured.filter((c) => !maleIds.has(c.id))
  // Interleave: M, F, M, F, M, F... (since audience is mostly male)
  const result: ClientPersona[] = []
  const maxLen = Math.max(males.length, females.length)
  for (let i = 0; i < maxLen; i++) {
    if (males[i]) result.push(males[i])
    if (females[i]) result.push(females[i])
  }
  return result
}

/** Get clients by niche */
export const getClientsByNiche = (niche: ClientNiche) =>
  CLIENTS.filter((c) => c.niche === niche)

/** Get clients with full case studies */
export const getCaseStudyClients = () => CLIENTS.filter((c) => c.hasCaseStudy)

/** Get minimal avatar data only (for carousels) */
export const getAvatarsOnly = () =>
  CLIENTS.map((c, i) => ({
    id: i,
    name: c.name,
    avatar: c.avatar,
    handle: c.handle,
  }))

/** Get clients by tier */
export const getClientsByTier = (tier: ClientTier) =>
  CLIENTS.filter((c) => c.tier === tier)

// ============================================================================
// LEGACY COMPATIBILITY EXPORTS
// These map to old data shapes used by existing components
// ============================================================================

/** For navigation.ts testimonials carousel */
export const getTestimonialsForNav = () => {
  // Hardcoded company logos that rotate through testimonials
  const companyLogos = [
    { logo: "/img/testimonials/logo_wigiwama.avif", logoWidth: 112 },
    { logo: "/img/testimonials/logo_burga.avif", logoWidth: 112 },
    { logo: "/img/testimonials/logo_regalrose.avif", logoWidth: 99 },
    { logo: "/img/testimonials/logo_nordvpn.png", logoWidth: 115.5 },
    { logo: "/img/testimonials/logo_trendhim.png", logoWidth: 113.5 },
  ]

  return getFeaturedClients()
    .slice(0, 5)
    .map((c, i) => ({
      id: i,
      logo: companyLogos[i].logo,
      logoWidth: companyLogos[i].logoWidth,
      quote: c.testimonial?.quote || "",
      author: c.name.split(" ")[0] + " " + (c.name.split(" ")[1]?.[0] || "") + ".",
      role: c.niche.charAt(0).toUpperCase() + c.niche.slice(1) + " Creator",
      avatar: c.avatar,
      bgColor: c.style.bgColor,
    }))
}

/** For reviewsData.ts reviews grid */
export const getReviewsForGrid = () =>
  CLIENTS.filter((c) => c.testimonial?.body).map((c) => ({
    id: c.id,
    title: c.testimonial?.title || "",
    body: c.testimonial?.body || "",
    rating: 5 as const,
    author: {
      name: c.name,
      avatar: c.avatar,
      handle: c.handle,
    },
    niche: c.niche,
    source: c.testimonial?.source || "google",
    featured: c.featured,
    metrics: c.metrics
      ? {
          followers: `+${(c.metrics.followersAfter - c.metrics.followersBefore).toLocaleString()}`,
          timeframe: `${Math.round(c.metrics.timeframeDays / 30)} months`,
        }
      : undefined,
  }))

/** For caseStudies.ts results page */
export const getCaseStudiesForResults = () =>
  getCaseStudyClients().map((c) => ({
    slug: c.seoSlug || c.id, // Use SEO-optimized slug when available
    name: c.name,
    handle: c.handle,
    niche: c.niche.charAt(0).toUpperCase() + c.niche.slice(1),
    nicheColor: c.style.nicheColor,
    avatar: c.avatar,
    heroImage: `/api/case-studies/hero?slug=${c.seoSlug || c.id}&v=4`,
    headline: c.caseStudy?.headline || "",
    excerpt: c.caseStudy?.excerpt || "",
    metrics: {
      followers: c.metrics
        ? `${Math.round((c.metrics.followersAfter - c.metrics.followersBefore) / 1000)}K`
        : "0K",
      followersLabel: "New followers",
      engagement: c.metrics?.engagementMultiplier
        ? `${c.metrics.engagementMultiplier}x`
        : "3x",
      engagementLabel: "Engagement increase",
      timeframe: c.metrics
        ? `${Math.round(c.metrics.timeframeDays / 30)} months`
        : "3 months",
      timeframeLabel: "Time to results",
    },
    about: c.caseStudy?.about || "",
    content: c.caseStudy?.content || {
      intro: "",
      challenge: "",
      solution: "",
      results: "",
    },
    quote: {
      text: c.testimonial?.quote || "",
      author: c.name,
      role: c.niche.charAt(0).toUpperCase() + c.niche.slice(1) + " Creator",
    },
  }))

/** For AccountsCarousel - random sample of avatars */
export const getCarouselAccounts = () =>
  CLIENTS.slice(0, 10).map((c, i) => ({
    id: i,
    avatar: c.avatar,
    name: c.name,
    type: c.niche.charAt(0).toUpperCase() + c.niche.slice(1),
  }))

/** For SocialProofPanel - 4 testimonials */
export const getSocialProofTestimonials = () =>
  getFeaturedClients()
    .slice(0, 4)
    .map((c) => ({
      name: c.name,
      handle: c.handle,
      avatar: c.avatar,
      quote: c.testimonial?.quote || "",
    }))

/** Get single demo client for GrowthPreviewCard (most aspirational female) */
export const getDemoClient = () => {
  const featured = CLIENTS.find(
    (c) =>
      c.featured &&
      c.tier === "mid" &&
      ["fashion", "lifestyle", "beauty"].includes(c.niche)
  )
  return featured || CLIENTS[0]
}
